// ─────────────────────────────────────────────────────────────────────────────
// CallGateThree.tsx — THE CALL GATE, THREE QUESTIONS, ON THE CALLS TAB.
//
//   Was the call made?  NO  → next attempt (date + time)
//                       YES → Was it successful?  NO  → saved, declined
//                                                 YES → when? (date + time)
//   and, once the call was made, what they said — optional.
//
// IT ANSWERS THE ROW THE OWNER PRESSED, AND ONLY WHILE THAT ROW'S COMPANY IS
// SELECTED. The row lives in `lib/deltaCallRow`; `artefactFor` checks it against
// the selection at the render, so switching company closes the gate rather than
// answering a call for the wrong business.
//
// NOTHING FIRES ON ITS OWN. The one POST is the owner's SAVE, and it cannot be
// sent twice while the first is in flight.
//
// AFTER A 200 THE ROW IS LET GO. The producer issues the NEXT `call_id` for the
// next attempt, so the pressed row is stale the moment the answer lands; the
// board is re-read at once and the owner presses the fresh row to answer again.
//
// EVERY DECISION IS IN `lib/deltaCallGate`, WITH TESTS. This file renders.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { MC } from '../missioncontrol/theme';
import { CallRow, getCallRow, setCallRow, subscribeCallRow } from '../../lib/deltaCallRow';
import { Selection, artefactFor, getSelection, subscribeSelection } from '../../lib/deltaSelection';
import {
  CallGateAnswers, CallGateBody, CallGateResult, NO_ANSWERS, buildCallGateBody, isCallGateRefusal,
} from '../../lib/deltaCallGate';
import { postCallGate } from '../../api/deltaCallGate';

export interface CallGateThreeProps {
  /** Re-reads the board — the list, the outcomes and the count move at once. */
  refresh?: () => void;
  /** Injected in tests; the live screen posts. */
  post?: (body: CallGateBody) => Promise<CallGateResult>;
}

const panel: React.CSSProperties = {
  background: MC.panel, borderRadius: MC.radius, padding: 18, boxShadow: MC.cardShadow,
};
const question: React.CSSProperties = { color: MC.text, fontWeight: 600, margin: '12px 0 8px' };
const picker: React.CSSProperties = {
  marginLeft: 10, background: MC.bg2, color: MC.text, colorScheme: 'dark',
  border: `1px solid ${MC.line2}`, borderRadius: 8, padding: '6px 10px', font: 'inherit',
};

function Choice({ label, on, onClick, name }: {
  label: string; on: boolean; onClick: () => void; name: string;
}) {
  return (
    <button type="button" aria-label={name} aria-pressed={on} onClick={onClick}
      style={{
        background: on ? MC.tealSoft : 'transparent',
        border: `1px solid ${on ? MC.tealEdge : MC.line2}`,
        color: on ? MC.teal : MC.text,
        borderRadius: 10, padding: '8px 22px', font: 'inherit', cursor: 'pointer',
      }}>{label}</button>
  );
}

export default function CallGateThree(props: CallGateThreeProps) {
  const [selection, setSel] = React.useState<Selection>(getSelection());
  const [pressed, setPressed] = React.useState<CallRow | null>(getCallRow());
  React.useEffect(() => subscribeSelection(setSel), []);
  React.useEffect(() => subscribeCallRow(setPressed), []);

  const [answers, setAnswers] = React.useState<CallGateAnswers>(NO_ANSWERS);
  const [busy, setBusy] = React.useState(false);
  const [note, setNote] = React.useState<string>('');
  const [result, setResult] = React.useState<{ who: string; r: CallGateResult } | null>(null);
  const inFlight = React.useRef(false);

  // A NEW PRESS STARTS CLEAN — the answers belong to the row they were given for.
  const rowKey = pressed ? pressed.salesTaskId + '|' + (pressed.callId || '') : '';
  React.useEffect(() => {
    setAnswers(NO_ANSWERS);
    setNote('');
    if (rowKey) setResult(null);
  }, [rowKey]);

  const row = artefactFor(selection, pressed);
  const set = (patch: Partial<CallGateAnswers>) => { setNote(''); setAnswers((a) => ({ ...a, ...patch })); };

  const save = async () => {
    if (inFlight.current || !row) return;
    const body = buildCallGateBody(row, answers);
    if (isCallGateRefusal(body)) { setNote(body.message); return; }
    inFlight.current = true;
    setBusy(true);
    setNote('');
    let r: CallGateResult;
    try {
      r = await (props.post || postCallGate)(body);
    } catch {
      r = { phase: 'failed', error: 'no answer from the server' };
    }
    inFlight.current = false;
    setBusy(false);
    if (r.phase === 'written') {
      setResult({ who: row.who, r });
      setCallRow(null);
      if (props.refresh) props.refresh();
    } else {
      setNote(r.error || r.phase);
      // A LOST ANSWER MAY HAVE LANDED — re-read so the list shows the truth.
      if (r.phase === 'failed' && props.refresh) props.refresh();
    }
  };

  if (!row) {
    return (
      <div style={panel}>
        <div style={{ color: MC.text, fontWeight: 600 }}>CALL GATE</div>
        {result && result.r.phase === 'written' ? (
          <div style={{ marginTop: 10, fontSize: 13 }}>
            <div style={{ color: MC.green }}>
              SAVED · {result.who || 'call'} · {result.r.event || '—'}
            </div>
            {result.r.unwritten.map((u) => (
              <div key={u.name} style={{ color: MC.amber, marginTop: 6 }}>
                NOT WRITTEN · {u.name}{u.ground ? ' — ' + u.ground : ''}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: MC.dim, marginTop: 6, fontSize: 13 }}>press a call in today&apos;s list</div>
        )}
      </div>
    );
  }

  const { made, successful } = answers;
  const showWords = made === true && successful !== null;
  const ready = made === false || (made === true && successful !== null);

  return (
    <div style={panel} aria-label="call gate">
      <div style={{ color: MC.text, fontWeight: 600 }}>
        CALL GATE · <span style={{ color: MC.teal }}>{row.who || '—'}</span>
        <span style={{ color: MC.dim, fontWeight: 400 }}>{row.when ? ' · ' + row.when : ''}</span>
      </div>

      <div style={question}>Was the call made?</div>
      <div style={{ display: 'flex', gap: 10 }}>
        <Choice name="call made yes" label="YES" on={made === true}
          onClick={() => set({ made: true })} />
        <Choice name="call made no" label="NO" on={made === false}
          onClick={() => set({ made: false, successful: null })} />
      </div>

      {made === false && (
        <label style={{ display: 'block', color: MC.muted, fontSize: 12, marginTop: 14 }}>
          Next attempt
          <input type="datetime-local" aria-label="next attempt date and time" style={picker}
            value={answers.nextAttemptLocal}
            onChange={(e) => set({ nextAttemptLocal: e.target.value })} />
        </label>
      )}

      {made === true && (
        <>
          <div style={question}>Was it successful?</div>
          <div style={{ display: 'flex', gap: 10 }}>
            <Choice name="successful yes" label="YES" on={successful === true}
              onClick={() => set({ successful: true })} />
            <Choice name="successful no" label="NO" on={successful === false}
              onClick={() => set({ successful: false })} />
          </div>
        </>
      )}

      {made === true && successful === true && (
        <label style={{ display: 'block', color: MC.muted, fontSize: 12, marginTop: 14 }}>
          Consultation
          <input type="datetime-local" aria-label="consultation date and time" style={picker}
            value={answers.agreedLocal}
            onChange={(e) => set({ agreedLocal: e.target.value })} />
        </label>
      )}

      {showWords && (
        <label style={{ display: 'block', color: MC.muted, fontSize: 12, marginTop: 14 }}>
          What did they say? <span style={{ color: MC.dim }}>(optional)</span>
          <textarea aria-label="what they said" rows={2} value={answers.words}
            onChange={(e) => set({ words: e.target.value })}
            style={{ ...picker, marginLeft: 0, marginTop: 6, display: 'block', width: '100%',
                     boxSizing: 'border-box', resize: 'vertical' }} />
        </label>
      )}

      {ready && (
        <div style={{ marginTop: 14 }}>
          <button type="button" onClick={save} disabled={busy}
            style={{
              background: busy ? 'transparent' : MC.tealSoft,
              border: `1px solid ${busy ? MC.line2 : MC.tealEdge}`,
              color: busy ? MC.dim : MC.teal,
              borderRadius: 10, padding: '8px 22px', font: 'inherit',
              cursor: busy ? 'default' : 'pointer',
            }}>{busy ? 'SAVING…' : 'SAVE'}</button>
        </div>
      )}

      {note && <div style={{ marginTop: 12, color: MC.red, fontSize: 13 }}>{note}</div>}
    </div>
  );
}
